import React, { useState, useEffect, useCallback } from 'react';
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import Tooltip from '@mui/material/Tooltip';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { useTheme } from '@mui/material/styles';
import { notificationApi, INotification } from '../../_api/notification.api';
import { NotificationType, getNotificationMessage } from './NotificationType';
import { NotificationDialog } from './NotificationDialog';
import { useAuth } from '../../_auth/useAuth';

/**
 * NotificationBell Component
 * 
 * Main notification component that displays the bell icon with badge count
 * and manages the notification dialog state.
 * 
 * Follows SOLID Principles:
 * - Single Responsibility: Manages notification bell UI and state
 * - Open/Closed: Can be extended through props without modification
 * - Dependency Inversion: Depends on abstract API interface
 */
export const NotificationBell: React.FC = () => {
    const theme = useTheme();
    const { getAccessToken } = useAuth();
    
    const [notifications, setNotifications] = useState<INotification[]>([]);
    const [unreadCount, setUnreadCount] = useState<number>(0);
    const [dialogOpen, setDialogOpen] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    // Track integer ids of notifications marked as read in this session
    const [readIdsThisSession, setReadIdsThisSession] = useState<number[]>([]);

    /**
     * Fetch notifications from API
     */
    const fetchNotifications = useCallback(async () => {
        // Using mock data for development
        // TODO: Uncomment below when backend API is ready
        
        try {
            const authToken = await getAccessToken();
            const response = await notificationApi.getNotifications(authToken);
            if (response.success && response.data) {
                // If response.data is an array, map it to notification objects
                const notifList: INotification[] = (Array.isArray(response.data) ? response.data : response.data.notifications || []).map((n: any) => ({
                    id: n.notification_id.toString(),
                    message: getNotificationMessage(n),
                    date: n.created_at,
                    isRead: n.is_read,
                }));
                setNotifications(notifList);
                // Use unreadCount from API if available, else count locally
                setUnreadCount(response.data.unreadCount ?? notifList.filter(n => !n.isRead).length);
            }
        } catch (error) {
            // Error fetching notifications
        }

    }, [getAccessToken]);

    /**
     * Load notifications on component mount
     */
    useEffect(() => {
        fetchNotifications();
    }, []);

    /**
     * Handle bell icon click
     */
    const handleBellClick = () => {
        // Fetch notifications from API when bell is clicked
        (async () => {
            try {
                const authToken = await getAccessToken();
                const response = await notificationApi.getNotifications(authToken);
                if (response.success && response.data) {
                    const notifList: INotification[] = (Array.isArray(response.data) ? response.data : response.data.notifications || []).map((n: any) => ({
                        id: n.notification_id.toString(),
                        message: getNotificationMessage(n),
                        date: n.created_at,
                        isRead: n.is_read,
                    }));
                    setNotifications(notifList);
                    setUnreadCount(response.data.unreadCount ?? notifList.filter(n => !n.isRead).length);
                }
            } catch (error) {
                // Error fetching notifications
            }
        })();
        setDialogOpen(true);
    };

    /**
     * Handle dialog close
     */
    const handleDialogClose = async () => {
        setDialogOpen(false);
        if (readIdsThisSession.length > 0) {
            try {
                const authToken = await getAccessToken();
                // Call markAsRead API with integer array
                await notificationApi.markAsRead({ notification_ids: readIdsThisSession }, authToken);
            } catch (error) {
                // Error marking notifications as read
            }
            setReadIdsThisSession([]);
        }
    };

    /**
     * Handle notification click
     */
    // On notification click, collect integer id, do not call API
    const handleNotificationClick = (notification: INotification) => {
        if (!notification.isRead) {
            setNotifications(prev =>
                prev.map(n =>
                    n.id === notification.id ? { ...n, isRead: true } : n
                )
            );
            setUnreadCount(prev => Math.max(0, prev - 1));
            const intId = parseInt(notification.id, 10);
            setReadIdsThisSession(prev => prev.includes(intId) ? prev : [...prev, intId]);
        }
    };

    /**
     * Handle mark all as read
     */
    // On mark all as read, collect all unread integer ids, do not call API
    const handleMarkAllAsRead = () => {
        const unreadIds = notifications
            .filter(n => !n.isRead)
            .map(n => parseInt(n.id, 10));

        if (unreadIds.length === 0) return;

        setLoading(true);
        setNotifications(prev =>
            prev.map(n => ({ ...n, isRead: true }))
        );
        setUnreadCount(0);
        setLoading(false);
        setReadIdsThisSession(prev => {
            const newIds = unreadIds.filter(id => !prev.includes(id));
            return [...prev, ...newIds];
        });
    };

    return (
        <>
            <Tooltip title="Notifications">
                <IconButton
                    aria-label="notifications"
                    onClick={handleBellClick}
                    sx={{
                        color: 'inherit',
                        padding: '6px',
                        '&:hover': { 
                            backgroundColor: theme.daPalette?.black?.light || 'rgba(255, 255, 255, 0.08)' 
                        },
                        marginRight: '1rem',
                    }}
                >
                    <Badge 
                        badgeContent={unreadCount} 
                        color="error"
                        max={99}
                    >
                        <NotificationsIcon />
                    </Badge>
                </IconButton>
            </Tooltip>

            <NotificationDialog
                open={dialogOpen}
                onClose={handleDialogClose}
                notifications={notifications}
                onNotificationClick={handleNotificationClick}
                onMarkAllAsRead={handleMarkAllAsRead}
            />
        </>
    );
};
